import React, { useEffect, useState } from 'react';
import { dashboardAPI, logsAPI } from '../../utils/api';
import { getModelDisplayName } from '../../utils/modelLabels';
import './DetectionsStatsModal.css';

export type DetectionsStatsData = {
  day: {
    total: number;
    by_model: Array<{ model: string; count: number }>;
  };
  week: {
    total: number;
    by_model: Array<{ model: string; count: number }>;
    by_day: Array<{ date: string; label: string; count: number }>;
  };
  by_area?: Array<{
    area_id: number;
    area_name: string;
    day: {
      total: number;
      by_model: Array<{ model: string; count: number }>;
    };
    week: {
      total: number;
      by_model: Array<{ model: string; count: number }>;
    };
  }>;
};

interface DetectionsStatsModalProps {
  /** Если не задан — статистика по всем участкам (главная). */
  areaId?: number;
  areaName: string;
  onClose: () => void;
}

const DetectionsStatsModal: React.FC<DetectionsStatsModalProps> = ({
  areaId,
  areaName,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<DetectionsStatsData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = areaId != null
          ? await logsAPI.getStatsByArea(areaId)
          : await dashboardAPI.getStats();
        if (cancelled) return;
        if (resp.success) {
          setStats({
            day: {
              total: resp.day.total,
              by_model: resp.day.by_model || [],
            },
            week: {
              total: resp.week.total,
              by_model: resp.week.by_model || [],
              by_day: resp.week.by_day || [],
            },
            ...('by_area' in resp && Array.isArray(resp.by_area)
              ? { by_area: resp.by_area }
              : {}),
          });
        } else {
          setError('Не удалось загрузить статистику');
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Ошибка загрузки статистики');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [areaId]);

  const weekMax = Math.max(1, ...(stats?.week.by_day.map((d) => d.count) ?? [1]));

  const renderModelBars = (
    items: Array<{ model: string; count: number }>,
    total: number
  ) => {
    if (items.length === 0) {
      return <p className="det-stats__empty">Нет детекций за период</p>;
    }
    const max = Math.max(1, ...items.map((i) => i.count));
    return (
      <ul className="det-stats__bars" aria-label="По типам детекций">
        {items.map((item) => {
          const pct = Math.round((item.count / max) * 100);
          const share = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <li key={item.model} className="det-stats__bar-row">
              <div className="det-stats__bar-meta">
                <span className="det-stats__bar-label">{getModelDisplayName(item.model)}</span>
                <span className="det-stats__bar-value">
                  {item.count}
                  <span className="det-stats__bar-share"> · {share}%</span>
                </span>
              </div>
              <div className="det-stats__bar-track" aria-hidden="true">
                <div className="det-stats__bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderAreaModelBars = (
    items: Array<{ model: string; count: number }>,
    total: number
  ) => {
    if (items.length === 0) {
      return <p className="det-stats__area-empty">Нет детекций за сегодня</p>;
    }
    const max = Math.max(1, ...items.map((i) => i.count));
    return (
      <ul className="det-stats__area-models">
        {items.map((item) => {
          const pct = Math.round((item.count / max) * 100);
          const share = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <li key={item.model} className="det-stats__area-model">
              <div className="det-stats__area-model-meta">
                <span className="det-stats__area-model-label">{getModelDisplayName(item.model)}</span>
                <span className="det-stats__area-model-value">
                  {item.count}
                  <span className="det-stats__area-model-share"> · {share}%</span>
                </span>
              </div>
              <div className="det-stats__area-model-track" aria-hidden="true">
                <div className="det-stats__area-model-fill" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="area-modal det-stats-modal" role="dialog" aria-modal="true" aria-labelledby="det-stats-title">
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog det-stats-modal__dialog">
        <header className="area-modal__header">
          <div>
            <h2 id="det-stats-title" className="area-modal__title">
              Статистика детекций
            </h2>
            <p className="det-stats__subtitle">
              {areaName}
              {areaId != null
                ? ' · все камеры'
                : ' · все участки'}
            </p>
          </div>
          <button type="button" className="area-modal__close" aria-label="Закрыть" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="det-stats-modal__body">
          {loading && (
            <div className="det-stats__loading">Загрузка статистики…</div>
          )}

          {!loading && error && (
            <div className="det-stats__error" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && stats && (
            <>
              <div className="det-stats__cards">
                <article className="det-stats__card det-stats__card--day">
                  <span className="det-stats__card-label">За сегодня</span>
                  <span className="det-stats__card-value">{stats.day.total}</span>
                  <span className="det-stats__card-hint">детекций с 00:00</span>
                </article>
                <article className="det-stats__card det-stats__card--week">
                  <span className="det-stats__card-label">За 7 дней</span>
                  <span className="det-stats__card-value">{stats.week.total}</span>
                  <span className="det-stats__card-hint">включая сегодня</span>
                </article>
              </div>

              <section className="det-stats__section" aria-label="Динамика за неделю">
                <h3 className="det-stats__section-title">Динамика за неделю</h3>
                <div className="det-stats__chart" role="img" aria-label="График детекций по дням">
                  {stats.week.by_day.map((d) => {
                    const h = Math.max(4, Math.round((d.count / weekMax) * 100));
                    return (
                      <div key={d.date} className="det-stats__chart-col">
                        <span className="det-stats__chart-value">{d.count > 0 ? d.count : ''}</span>
                        <div className="det-stats__chart-bar-wrap">
                          <div
                            className="det-stats__chart-bar"
                            style={{ height: `${h}%` }}
                            title={`${d.label}: ${d.count}`}
                          />
                        </div>
                        <span className="det-stats__chart-label">{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="det-stats__split">
                <section className="det-stats__section">
                  <h3 className="det-stats__section-title">Сегодня по типам</h3>
                  {renderModelBars(stats.day.by_model, stats.day.total)}
                </section>
                <section className="det-stats__section">
                  <h3 className="det-stats__section-title">Неделя по типам</h3>
                  {renderModelBars(stats.week.by_model, stats.week.total)}
                </section>
              </div>

              {areaId == null && stats.by_area && (
                <section className="det-stats__section det-stats__section--areas" aria-label="По участкам">
                  <h3 className="det-stats__section-title">По участкам</h3>
                  {stats.by_area.filter((a) => a.day.total > 0 || a.week.total > 0).length === 0 ? (
                    <p className="det-stats__empty">Нет детекций по участкам</p>
                  ) : (
                    <ul className="det-stats__areas">
                      {stats.by_area
                        .filter((area) => area.day.total > 0 || area.week.total > 0)
                        .map((area) => (
                          <li key={area.area_id} className="det-stats__area-card">
                            <div className="det-stats__area-top">
                              <div className="det-stats__area-title-wrap">
                                <span className="det-stats__area-icon-wrap" aria-hidden="true">
                                  <svg viewBox="0 0 24 24" fill="none" className="det-stats__area-icon">
                                    <path
                                      d="M3 9.5L12 4L21 9.5V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9.5Z"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M9 21V12H15V21"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                                <span className="det-stats__area-name">{area.area_name}</span>
                              </div>
                              <div className="det-stats__area-badges">
                                <span className="det-stats__area-badge det-stats__area-badge--day">
                                  <span className="det-stats__area-badge-label">Сегодня</span>
                                  <span className="det-stats__area-badge-value">{area.day.total}</span>
                                </span>
                                <span className="det-stats__area-badge det-stats__area-badge--week">
                                  <span className="det-stats__area-badge-label">Неделя</span>
                                  <span className="det-stats__area-badge-value">{area.week.total}</span>
                                </span>
                              </div>
                            </div>
                            <div className="det-stats__area-body">
                              <span className="det-stats__area-body-label">Сегодня по типам</span>
                              {renderAreaModelBars(area.day.by_model, area.day.total)}
                            </div>
                          </li>
                        ))}
                    </ul>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        <footer className="area-modal__footer">
          <button
            type="button"
            className="area-modal__submit area-modal__submit--secondary"
            onClick={onClose}
          >
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DetectionsStatsModal;
